import { useEffect, useState } from 'react';
import type { Order, Product, Ticket } from '@runninglime/shared';
import { apiGet } from '../../lib/api.ts';
import { LoadingSpinner } from '../../components/ui/Shared.tsx';

interface Stats {
  totalProducts: number;
  activeProducts: number;
  totalOrders: number;
  pendingOrders: number;
  totalTickets: number;
  openTickets: number;
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [prodRes, orderRes, ticketRes] = await Promise.all([
        apiGet<{ items: Product[]; total: number }>('/admin/products'),
        apiGet<{ items: Order[]; total: number }>('/admin/orders'),
        apiGet<{ items: Ticket[]; total: number }>('/admin/tickets'),
      ]);

      const products = prodRes.data?.items || [];
      const orders = orderRes.data?.items || [];
      const tickets = ticketRes.data?.items || [];

      setStats({
        totalProducts: products.length,
        activeProducts: products.filter((p) => p.active).length,
        totalOrders: orders.length,
        pendingOrders: orders.filter((o) => o.status === 'pending' || o.status === 'confirmed').length,
        totalTickets: tickets.length,
        openTickets: tickets.filter((t) => t.status === 'open' || t.status === 'in-progress-l1' || t.status === 'in-progress-l2').length,
      });
      setRecentOrders(orders.slice(0, 5));
      setLoading(false);
    };
    load();
  }, []);

  if (loading) return <LoadingSpinner />;

  const cards = [
    { label: 'Active Products', value: stats?.activeProducts ?? 0, sub: `${stats?.totalProducts ?? 0} total`, icon: '📦', color: 'from-lime/20 to-green/10' },
    { label: 'Pending Orders', value: stats?.pendingOrders ?? 0, sub: `${stats?.totalOrders ?? 0} total`, icon: '🛒', color: 'from-yellow-100 to-amber-50' },
    { label: 'Open Tickets', value: stats?.openTickets ?? 0, sub: `${stats?.totalTickets ?? 0} total`, icon: '💬', color: 'from-blue-50 to-indigo-50' },
  ];

  return (
    <div className="animate-fade-in">
      <h1 className="text-3xl font-bold text-dark mb-6">Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        {cards.map((c) => (
          <div key={c.label} className={`bg-gradient-to-br ${c.color} rounded-2xl p-6 hover-lift cursor-default`}>
            <div className="text-3xl mb-2">{c.icon}</div>
            <div className="text-3xl font-bold text-dark">{c.value}</div>
            <div className="text-sm font-medium text-dark/80">{c.label}</div>
            <div className="text-xs text-gray-500 mt-1">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-dark">Recent Orders</h2>
        </div>
        {recentOrders.length === 0 ? (
          <div className="px-6 py-8 text-center text-gray-400 text-sm">No orders yet</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentOrders.map((o) => (
              <div key={o.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200">
                <div>
                  <span className="font-mono text-sm text-dark">#{o.id.slice(0, 8)}</span>
                  <span className="text-gray-400 text-sm ml-3">{o.items.length} item{o.items.length !== 1 && 's'}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm font-medium text-dark">€{((o.total || 0) / 100).toFixed(2)}</span>
                  <span className={`px-2.5 py-0.5 text-xs rounded-full font-medium ${
                    o.status === 'delivered' ? 'bg-green/10 text-green' :
                    o.status === 'problem' ? 'bg-red-100 text-red-700' :
                    o.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-blue-50 text-blue-600'
                  }`}>
                    {o.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
